<template>
    <div class="learningpath">
        <Banner
            v-if="!!singleLearningPath"
            :duration="durationOfLearningPath"
            :learningPath="singleLearningPath"
            :courses="courses"
            :showFinishProgress="isLoggedIn"
            :hasAccessToLearningPath="hasAccessToLearningPath"
        />
        <div class="page-content" v-if="singleLearningPath">
            <div v-for="(learningPathItem, index) in learningPathCourses" :key="index" class="learningpath__item">
                <div class="learningpath__section" v-if="learningPathItem.itemType === 'paragraph'" >
                    <SingleLearningPathParagraph
                        :title="learningPathItem.title"
                        :description="learningPathItem.description"
                        :isSubscribed="singleLearningPath.subscribed"
                    />
                </div>
                <SingleLearningPathItem
                    :course="learningPathItem"
                    :learningPath="singleLearningPath"
                    :amountOfCourses="courses.length"
                    :index="learningPathItem.itemIndexInLearningPath"
                    :courseId="singleLearningPath.id"
                    :extendPathGraph="checkForParagraphInBetweenCourses(index)"
                    :isSubscribed="singleLearningPath.subscribed"
                    :hasAccessToLearningPath="hasAccessToLearningPath"
                />
            </div>
            <SingleLearningPathCertificate
                :course="singleLearningPath"
                :amountOfCourses="courses.length + 1"
                :isSubscribed="singleLearningPath.subscribed"
                :isLearningPathCompleted="isLearningPathCompleted"
                :learningPathId="singleLearningPath.id"
            />
        </div>
        <ModalAddToNewWatchlist/>
    </div>
</template>

<script>
    import Banner from 'components/learningpathpage/banner/LearningPathBanner'
    import SingleLearningPathItem from 'components/learningpathpage/learningpath-item/SingleLearningPathItem'
    import SingleLearningPathParagraph from 'components/learningpathpage/learningpath-item/SingleLearningPathParagraph'
    import SingleLearningPathCertificate from 'components/learningpathpage/learningpath-item/SingleLearningPathCertificate'
    import ModalAddToNewWatchlist from 'components/shared/modals/ModalAddToNewWatchlist'
    import { mapGetters } from 'vuex'
    import routeHelper from 'components/shared/mixins/routeHelper'

    export default {
        name: 'LearningPathPage',
        components: {
            Banner,
            SingleLearningPathItem,
            ModalAddToNewWatchlist,
            SingleLearningPathParagraph,
            SingleLearningPathCertificate
        },
        mixins: [routeHelper],
        metaInfo () {
            return {
                title: this.$t('LearningPath.course_learningpath') + ' ' + this.learningPathTitle + ' - Eduhouse'
            }
        },
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                singleLearningPath: 'LearningPaths/getSingleLearningPath',
                getCourseById: 'Courses/getCourseById',
                shouldReloadLearningPaths: 'CompletedCourses/shouldReloadLearningPaths',
                currentUser: 'User/getCurrentUser'
            }),
            learningPathCourses () {
                let itemIndexInLearningPath = 0
                return this.singleLearningPath.items.map((item) => {
                    if (item.type === 'course') {
                        itemIndexInLearningPath++
                        return { ...this.getCourseById(item.id), itemType: item.type, itemIndexInLearningPath: itemIndexInLearningPath }
                    } else {
                        return { ...item, itemType: item.type }
                    }
                })
            },
            learningPathTitle () {
                return (this.singleLearningPath) ? this.singleLearningPath.title : ''
            },
            courses () {
                return this.singleLearningPath.items.filter(el => el.type === 'course')
            },
            durationOfLearningPath () {
                return this.courses.map(course => this.getCourseById(course.id)).filter(el => !!el).reduce((acc, course) => {
                    return acc + +course.videoDuration
                }, 0)
            },
            isLearningPathCompleted () {
                return this.singleLearningPath.hasUserCertificate || this.singleLearningPath.canUserCreateCertificate
            },
            hasAccessToLearningPathWithDigitalClassrooms () {
                let digitalClassrooms = this.learningPathCourses.filter(item => item.itemType === 'virtual_classroom')
                if (digitalClassrooms.length === 0) {
                    return true
                }
                return this.currentUser.hasAccessToDigitalClassrooms
            },
            hasAccessToLearningPath () {
                return this.isLoggedIn && !this.isDemo && this.hasAccessToLearningPathWithDigitalClassrooms
            },
            isDemo () {
                return this.singleLearningPath.demo
            }
        },
        methods: {
            async refreshData () {
                try {
                    await Promise.all(
                        [
                            this.$store.dispatch('LearningPaths/loadSingleLearningPath', this.$route.params.learningPathSlug),
                            this.$store.dispatch('Courses/loadLearningPathCoursesBySlug', this.$route.params.learningPathSlug),
                        ]
                    )
                    if (this.isLoggedIn) {
                        await this.$store.dispatch('VideoLog/loadVideoLogs')
                    }
                } catch (e) {
                    if (e.code === 404 || e.code === 320) {
                        this.$router.push({ name: 'page-not-found' })
                    }
                    if (e.code === 403 || e.code === 401 ||
                        e.response.status === 403 || e.response.status === 401) {
                        if (this.isLoggedIn) {
                            await this.$router.push({ name: 'page-no-permission' })
                        } else {
                            await this.$router.push({ name: 'login' })
                        }
                    }
                    if (e.code === 410 || e.response.status === 410) {
                        await this.$router.push({ name: 'page-not-found' })
                    }
                    throw e
                }
            },
            checkForParagraphInBetweenCourses (index) {
                if (index === this.learningPathCourses.length - 1) {
                    return false
                }
                return this.learningPathCourses[index + 1].itemType === 'paragraph'
            },
            async updateLearningPathPage () {
                await this.$store.dispatch('CompletedCourses/loadCertificates')
                await this.$store.dispatch('LearningPaths/loadSingleLearningPath', this.$route.params.learningPathSlug)
                await this.$store.dispatch('CompletedCourses/setReloadLearningPathsState', false)
                this.$forceUpdate()
            }
        },
        async created () {
            const taskName = 'loadingLearningPath'
            try {
                await this.$store.dispatch('LoaderScreen/addTaskToLoader', {
                    key: taskName,
                    message: 'Loading LearningPath'
                })
                await this.refreshData()
                if (this.singleLearningPath.status === 'draft' || this.singleLearningPath.status === 'expired') {
                    this.$router.push({ name: 'page-not-found' })
                }
            } catch (e) {
            }
            this.$store.dispatch('LoaderScreen/removeTaskFromLoader', taskName)
            this.checkAndSetNewUrlSlug(this.singleLearningPath, 'learningPath')
        },
        beforeDestroy () {
            this.$store.dispatch('LearningPaths/clearSingleLearningPath')
        },
        watch: {
            shouldReloadLearningPaths: {
                async handler (newValue, oldValue) {
                    if (newValue === true) {
                        await this.updateLearningPathPage()
                    }
                },
                immediate: true
            }
        }
    }
</script>

<style lang="scss" scoped>
    .learningpath {
        .page-content {
            padding: 50px 20px;
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;

            [class*="mobile"] & {
                flex-direction: row;
                padding: 50px 0;
                margin: 0;
            }

            [class*="mobile_sm"] & {
                padding: 50px 20px;
            }
        }

        &__item {
            flex: 1;

            [class*="mobile_md"] & {
                border: none;
                margin: calc($gap / 2);
                flex: 0 0 calc(50% - #{$gap});
            }

            [class*="mobile_sm"] & {
                flex: 0 0 100%;
                margin: 0 0 24px;
            }
        }

        &__section {
            [class*="desktop"] & {
                margin: 0 0 25px 65px;
            }
        }

        &__next-paths {
            display: flex;

            [class*="desktop"] & {
                flex-direction: row;
            }
            flex-direction: column;
            justify-content: flex-start;
        }
    }
</style>
